/**
 * agentAPI.js — Smart data layer for CUA Benchmark Arena
 *
 * Strategy: Try Cloudflare D1 API first (/api/*). If unavailable
 * (local dev, network error), fall back to localStorage via agentDB.js.
 * This ensures the app works both locally and in production.
 *
 * In production (Cloudflare Pages + D1):  all data is global & persistent.
 * In local dev (npm start):              falls back to localStorage seamlessly.
 */

import * as db from './agentDB';

const BASE = '/api';
let _useAPI = null; // null = not yet probed, true/false after first call
let _probePromise = null; // deduplicate concurrent probes

const TIMEOUT_MS = 5000; // 5 second timeout for all API calls

function withTimeout(fetchPromise, ms = TIMEOUT_MS) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), ms);
  // We can't cancel the original fetch, but we race it against a timeout rejection
  return Promise.race([
    fetchPromise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Request timed out')), ms)
    ),
  ]).finally(() => clearTimeout(timer));
}

async function probeAPI() {
  if (_useAPI !== null) return _useAPI;
  if (_probePromise) return _probePromise;
  _probePromise = (async () => {
    try {
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
      const res = await fetch(`${BASE}/seed`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
        signal: ctrl.signal,
      }).finally(() => clearTimeout(timer));
      // React dev server returns text/html for unknown routes (SPA catch-all).
      // Real Pages Functions return application/json.
      const ct = res.headers.get('content-type') || '';
      if (!ct.includes('application/json')) {
        _useAPI = false;
        return false;
      }
      _useAPI = true;
      const data = await res.json();
      // If D1 is empty, seed it
      if (data && data.agentCount === 0) {
        fetch(`${BASE}/seed`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        }).catch(() => {});
      }
    } catch {
      _useAPI = false;
    }
    _probePromise = null;
    return _useAPI;
  })();
  return _probePromise;
}

async function request(path, opts = {}, timeoutMs = TIMEOUT_MS) {
  const { headers: extraHeaders, signal: _ignore, ...rest } = opts;
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await fetch(`${BASE}${path}`, {
      headers: { 'Content-Type': 'application/json', ...extraHeaders },
      signal: ctrl.signal,
      ...rest,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(err.error || `API error ${res.status}`);
    }
    return res.json();
  } finally {
    clearTimeout(timer);
  }
}

function authHeaders() {
  const sess = db.getSession();
  if (sess && sess.agentKey) return { Authorization: `Bearer ${sess.agentKey}` };
  return {};
}

// Re-export security helpers from agentDB (shared client-side)
export { AGENT_KEY_RE } from './agentDB';

// ── Session (always localStorage — same in both modes) ───────────────────
export { getSession, setSession, clearSession, validateSession } from './agentDB';

// ── Agents ─────────────────────────────────────────────────────────────────
export async function getAllAgents() {
  if (await probeAPI()) return request('/agents');
  return db.getAllAgents();
}

export async function getAgent(id) {
  if (await probeAPI()) return request(`/agents?id=${encodeURIComponent(id)}`);
  return db.getAgent(id);
}

export async function getAgentByKey(key) {
  if (await probeAPI()) return request(`/agents?key=${encodeURIComponent(key)}`);
  return db.getAgentByKey(key);
}

export async function registerAgent({ name, model, version, framework, owner, country }) {
  if (await probeAPI()) {
    return request('/agents', {
      method: 'POST',
      body: JSON.stringify({ name, model, version, framework, owner, country }),
    });
  }
  return db.registerAgent({ name, model, version, framework, owner, country });
}

// ── Runs ──────────────────────────────────────────────────────────────────
export async function getAllRuns() {
  if (await probeAPI()) return request('/runs');
  return db.getAllRuns();
}

export async function getRunsByAgent(agentId) {
  // If a session exists, go straight to D1 — don't wait for probeAPI
  const sess = db.getSession();
  if (sess) {
    try {
      return await request(`/runs?agentId=${encodeURIComponent(agentId)}`, {
        headers: authHeaders(),
      });
    } catch {
      // fall through to localStorage if API is unreachable
    }
  }
  if (await probeAPI()) return request(`/runs?agentId=${encodeURIComponent(agentId)}`);
  return db.getRunsByAgent(agentId);
}

export async function saveRun(run) {
  if (await probeAPI()) {
    return request('/runs', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(run),
    });
  }
  return db.saveRun(run);
}

// ── Leaderboard ───────────────────────────────────────────────────────────
export async function getLeaderboard(limit) {
  if (await probeAPI()) return request(`/leaderboard${limit ? `?limit=${limit}` : ''}`);
  const board = db.getLeaderboard();
  return limit ? board.slice(0, limit) : board;
}

// ── Stats ─────────────────────────────────────────────────────────────────
export async function getStats() {
  if (await probeAPI()) return request('/stats');
  const agents = db.getAllAgents();
  const runs = db.getAllRuns();
  const countries = new Set(agents.map(a => a.country).filter(Boolean)).size;
  return { agents: agents.length, runs: runs.length, countries };
}

// ── Draft Runs (pause/resume) — D1 only, no localStorage fallback ─────────
export async function saveDraft(draft) {
  return request('/draft-run', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(draft),
  }, 10000);
}

export async function loadDraft(agentId) {
  try {
    return await request(`/draft-run?agentId=${encodeURIComponent(agentId)}`, {}, 8000);
  } catch (e) {
    // 404 means no draft exists — not an error
    if (e.message && (e.message.includes('404') || e.message.toLowerCase().includes('not found') || e.message.toLowerCase().includes('no draft'))) {
      return null;
    }
    throw e;
  }
}

export async function clearDraft(agentId) {
  return request(`/draft-run?agentId=${encodeURIComponent(agentId)}`, {
    method: 'DELETE',
    headers: authHeaders(),
  }, 10000);
}

// ── Seed ──────────────────────────────────────────────────────────────────
export async function seedDemoData() {
  if (await probeAPI()) {
    try { return await request('/seed', { method: 'POST' }); } catch { /* ignore */ }
  }
  return db.seedDemoData();
}
